import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Flex,
  Input,
  Textarea,
  Button,
  HStack,
  VStack,
  useToast,
  useDisclosure,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Spinner,
  Text,
  Heading,
  Badge,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  ButtonGroup,
  Icon,
  Switch,
  FormControl,
  FormLabel,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import { FiChevronRight, FiSave, FiList, FiGrid, FiPlay, FiXCircle, FiDownload, FiUpload, FiCopy } from 'react-icons/fi';
import { useCampaignBuilder } from '../../../../hooks/useCampaignBuilder';
import { toggleUserCampaign } from '../../../../api/supabase/automations';
import { useAuth } from '../../../../contexts/AuthContext';
import { useImpersonation } from '../../../../contexts/ImpersonationContext';
import {
  isPersonalTemplate,
  isCompanyAutomation as isCompanyAutomationTemplate,
  canOpenTemplateInBuilder,
  canViewTemplate,
} from '../../../../utils/campaignAccess';
import CampaignContext from './CampaignContext';
import TriggerHeader, { AUTOMATION_EXCLUDED_TRIGGERS } from './TriggerHeader';
import NodeList from './NodeList';
import CanvasBuilder from './CanvasBuilder';
import NodeConfigDrawer from './NodeConfigDrawer';
import ContactTestPicker from './ContactTestPicker';
import ImportCampaignModal from './ImportCampaignModal';
import DuplicateCampaignModal from '../DuplicateCampaignModal';
import { useAgentPage } from '../../../../components/FutureBot/FutureBotProvider';
import { useBuilderAgentTools } from './useBuilderAgentTools';

export default function CampaignBuilderPage({ readOnly = false }) {
  const { templateId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const { currentUser } = useAuth();
  const {
    effectiveUserId,
    effectiveIsAdmin,
    isImpersonating,
    effectiveUserName,
    canImpersonate,
    impersonatedUser,
  } = useImpersonation();

  // Kind for NEW campaigns: admins default to Company and get a selector with
  // Company / Personal / Automation (company-wide + hidden); everyone else —
  // including an admin impersonating a non-admin — builds a Personal campaign.
  const [kindOverride, setKindOverride] = useState(null);
  const campaignKind = kindOverride ?? (effectiveIsAdmin ? 'company' : 'user');
  const ownershipType = campaignKind === 'user' ? 'user' : 'company';

  // effectiveUserId (not currentUser.uid): while impersonating, new campaigns
  // are created for — and auto-subscribed to — the impersonated user.
  const builder = useCampaignBuilder(templateId || null, effectiveUserId, {
    ownershipType,
    executionScope: campaignKind === 'automation' ? 'company' : 'user',
  });
  const {
    template, nodes, loading, saving, error, isDirty,
    setTemplateMeta, addNode, removeNode, updateNodeConfig,
    updateNodeSubType, moveNode, setFalseNodeId, save, importCampaign,
    testResults, testContact, testRunning, triggerTestResult, fullTestResult,
    setTestContact, testTrigger, testNode, testFullRun, clearTestResults, executeNode,
  } = builder;

  // Existing templates carry their scope in the loaded row; new ones follow
  // the selector. Scope is immutable after creation (no edit control exists).
  const isAutomation = template?.id
    ? isCompanyAutomationTemplate(template)
    : campaignKind === 'automation';

  const [selectedNode, setSelectedNode] = useState(null);
  const [builderMode, setBuilderMode] = useState('simple');
  const [enablePrompt, setEnablePrompt] = useState(null);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const cancelRef = useRef();
  const drawer = useDisclosure();
  const importModal = useDisclosure();
  const bgPage = useColorModeValue('gray.50', 'gray.900');
  const bannerBg = useColorModeValue('green.50', 'green.900');
  const bannerBorder = useColorModeValue('green.200', 'green.700');

  // Unsaved-changes navigation guard
  const leaveCancelRef = useRef();
  const [pendingNav, setPendingNav] = useState(null);
  const [showLeave, setShowLeave] = useState(false);
  const isDirtyRef = useRef(isDirty);
  const bypassRef = useRef(false);
  const leaveOpenRef = useRef(false);

  // Keep a ref of the dirty flag so the (once-registered) blocker reads fresh state
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  // Intercept in-app navigation when there are unsaved changes and show a custom popup.
  // Returning `false` from the block callback cancels navigation silently (no native confirm),
  // so we can render our own AlertDialog instead.
  useEffect(() => {
    if (readOnly) return undefined;
    const unblock = history.block((location) => {
      if (bypassRef.current) { bypassRef.current = false; return undefined; } // allow one nav through
      if (!isDirtyRef.current) return undefined;                              // nothing to lose
      if (leaveOpenRef.current) return false;                                // popup already showing
      leaveOpenRef.current = true;
      setPendingNav(location);
      setShowLeave(true);
      return false;                                                          // block this attempt
    });
    return unblock;
  }, [history, readOnly]);

  // Native guard for tab close / refresh / hard navigation
  useEffect(() => {
    if (readOnly) return undefined;
    const handler = (e) => {
      if (!isDirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [readOnly]);

  const handleStay = () => {
    setShowLeave(false);
    leaveOpenRef.current = false;
    setPendingNav(null);
  };

  const handleLeave = () => {
    setShowLeave(false);
    leaveOpenRef.current = false;
    bypassRef.current = true;
    const target = pendingNav;
    setPendingNav(null);
    if (target) {
      history.push(`${target.pathname}${target.search || ''}${target.hash || ''}`);
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    drawer.onOpen();
  };

  // Shared post-save bookkeeping (used by the Save button AND FutureBot's
  // save_campaign tool): swap the URL onto the new template id so subsequent
  // saves update instead of duplicating, and offer to enable new campaigns.
  const handlePostSave = useCallback((saved) => {
    if (!templateId && saved?.id) {
      bypassRef.current = true; // don't intercept the post-save URL swap
      history.replace(`/crm/campaigns/builder/${saved.id}`);
    }
    if (saved?._isNew && saved?._userCampaignId) {
      setEnablePrompt({
        userCampaignId: saved._userCampaignId,
        isAutomation: saved?.executionScope === 'company',
      });
    }
  }, [templateId, history]);

  // Ownership access guard (direct-URL protection): personal campaigns open
  // only for their creator or a real admin impersonating the creator; company
  // campaigns are edit-locked to admins (read-only view stays open to all).
  const accessCtx = {
    uid: currentUser?.uid,
    realAdmin: canImpersonate,
    impersonatedUid: impersonatedUser?.uid,
  };
  const accessDenied =
    !!templateId && !loading && !!template?.id &&
    !(readOnly ? canViewTemplate(template, accessCtx) : canOpenTemplateInBuilder(template, accessCtx));

  // FutureBot integration: register this page's agent tools + context.
  const { registration, agentTouchedIds, agentBuildStamp } = useBuilderAgentTools(builder, {
    enabled: !readOnly && !accessDenied,
    onSaved: handlePostSave,
  });
  useAgentPage(registration);

  const handleSave = async () => {
    try {
      const saved = await save();
      toast({
        title: 'Campaign saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handlePostSave(saved);
    } catch (saveErr) {
      toast({
        title: 'Save failed',
        description: saveErr.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (accessDenied) {
    const personal = isPersonalTemplate(template);
    return (
      <Box bg={bgPage} minH="100vh">
        <Container maxW="container.md" pt={12}>
          <Alert status="warning" borderRadius="lg" alignItems="start" p={5}>
            <AlertIcon />
            <VStack align="start" spacing={3}>
              <Text fontWeight="bold">
                {personal
                  ? 'This is a personal campaign'
                  : 'Company campaigns can only be edited by admins'}
              </Text>
              <Text fontSize="sm">
                {personal
                  ? (canImpersonate
                      ? 'Impersonate its owner from the Campaigns page ("View as user") to open it.'
                      : 'Only its creator can open it.')
                  : 'You can open a read-only view of this campaign instead.'}
              </Text>
              <HStack>
                <Button as={RouterLink} to="/crm/campaigns" size="sm" colorScheme="blue">
                  Back to Campaigns
                </Button>
                {!personal && (
                  <Button
                    as={RouterLink}
                    to={`/crm/campaigns/view/${template.id}`}
                    size="sm"
                    variant="outline"
                  >
                    View campaign
                  </Button>
                )}
              </HStack>
            </VStack>
          </Alert>
        </Container>
      </Box>
    );
  }

  const contextValue = {
    template, nodes, setTemplateMeta,
    addNode, removeNode, updateNodeConfig,
    updateNodeSubType, moveNode, setFalseNodeId,
    testResults, testContact, testRunning, triggerTestResult, fullTestResult,
    setTestContact, testTrigger, testNode, testFullRun, clearTestResults, executeNode,
    readOnly,
    // Company Automation scope — TriggerHeader restricts the trigger picker.
    isCompanyAutomation: isAutomation,
    // FutureBot visual feedback (NodeCard glow + staggered build reveal)
    agentTouchedIds, agentBuildStamp,
  };

  return (
    <CampaignContext.Provider value={contextValue}>
      <Box bg={bgPage} minH="100vh" pb={10}>
        <Container maxW="container.xl" pt={6}>
          {/* Header */}
          <Breadcrumb spacing="8px" separator={<FiChevronRight color="gray.500" />} mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/crm/campaigns">Campaigns</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>{template?.name || 'New Campaign'}</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {!readOnly && isImpersonating && (
            <Alert status="info" mb={4} borderRadius="md" py={2}>
              <AlertIcon boxSize={4} />
              <Text fontSize="sm">
                Building for <Text as="span" fontWeight="bold">{effectiveUserName}</Text> — this campaign will belong to them.
              </Text>
            </Alert>
          )}

          <Flex justify="space-between" align="start" mb={6} wrap="wrap" gap={4}>
            <VStack align="start" spacing={2} flex={1} minW="250px" data-tour="builder-name">
              {readOnly ? (
                <>
                  <Heading size="lg">{template?.name || 'Campaign'}</Heading>
                  {template?.description && (
                    <Text fontSize="sm" color="gray.500">{template.description}</Text>
                  )}
                  <HStack spacing={2}>
                    {template?.enrollmentRules?.allow_re_enrollment && (
                      <Badge colorScheme="orange" fontSize="xs" variant="subtle">Re-enrollment enabled</Badge>
                    )}
                    {template?.id && (
                      <Badge
                        colorScheme={isAutomation ? 'purple' : isPersonalTemplate(template) ? 'gray' : 'blue'}
                        fontSize="xs"
                        variant="subtle"
                      >
                        {isAutomation
                          ? 'Company automation'
                          : isPersonalTemplate(template) ? 'Personal campaign' : 'Company campaign'}
                      </Badge>
                    )}
                  </HStack>
                </>
              ) : (
                <>
                  <Input
                    variant="flushed"
                    fontSize="2xl"
                    fontWeight="bold"
                    placeholder="Campaign Name"
                    value={template?.name || ''}
                    onChange={(e) => setTemplateMeta({ name: e.target.value })}
                  />
                  <Textarea
                    variant="flushed"
                    placeholder="Description (optional)"
                    size="sm"
                    rows={1}
                    value={template?.description || ''}
                    onChange={(e) => setTemplateMeta({ description: e.target.value })}
                  />
                  <FormControl display="flex" alignItems="center" mt={1}>
                    <Tooltip
                      label="When enabled, this campaign can trigger multiple times for the same contact. Useful for automation-style workflows."
                      fontSize="xs"
                      placement="right"
                      hasArrow
                    >
                      <FormLabel mb={0} fontSize="xs" color="gray.500" cursor="help">
                        Allow re-enrollment
                      </FormLabel>
                    </Tooltip>
                    <Switch
                      size="sm"
                      colorScheme="orange"
                      isChecked={template?.enrollmentRules?.allow_re_enrollment === true}
                      onChange={(e) =>
                        setTemplateMeta({
                          enrollmentRules: {
                            ...(template?.enrollmentRules || {}),
                            allow_re_enrollment: e.target.checked,
                          },
                        })
                      }
                    />
                  </FormControl>
                  {!template?.id ? (
                    effectiveIsAdmin ? (
                      <VStack align="start" spacing={1} data-tour="builder-ownership">
                        <FormControl display="flex" alignItems="center" mt={1}>
                          <Tooltip
                            label="Company campaigns are visible to everyone. Personal campaigns are only visible to you. Automations run in the background on every user's deals and contacts, and only admins ever see them."
                            fontSize="xs"
                            placement="right"
                            hasArrow
                          >
                            <FormLabel mb={0} fontSize="xs" color="gray.500" cursor="help">
                              Campaign type
                            </FormLabel>
                          </Tooltip>
                          <ButtonGroup size="xs" isAttached variant="outline">
                            <Button
                              colorScheme={campaignKind === 'company' ? 'blue' : 'gray'}
                              variant={campaignKind === 'company' ? 'solid' : 'outline'}
                              onClick={() => setKindOverride('company')}
                            >
                              Company
                            </Button>
                            <Button
                              colorScheme={campaignKind === 'user' ? 'blue' : 'gray'}
                              variant={campaignKind === 'user' ? 'solid' : 'outline'}
                              onClick={() => setKindOverride('user')}
                            >
                              Personal
                            </Button>
                            <Button
                              colorScheme={campaignKind === 'automation' ? 'purple' : 'gray'}
                              variant={campaignKind === 'automation' ? 'solid' : 'outline'}
                              onClick={() => {
                                setKindOverride('automation');
                                const updates = {};
                                // Automations fire per event — default re-enrollment
                                // on unless the admin already chose otherwise.
                                if (template?.enrollmentRules?.allow_re_enrollment === undefined) {
                                  updates.enrollmentRules = {
                                    ...(template?.enrollmentRules || {}),
                                    allow_re_enrollment: true,
                                  };
                                }
                                // Manual/timed triggers are owner-scoped and not
                                // offered for automations — swap to a supported one.
                                if (AUTOMATION_EXCLUDED_TRIGGERS.has(template?.triggerType || 'MANUAL_ADD')) {
                                  updates.triggerType = 'PIPELINE_STAGE_CHANGED';
                                  updates.triggerConfig = {};
                                }
                                if (Object.keys(updates).length) setTemplateMeta(updates);
                              }}
                            >
                              Automation
                            </Button>
                          </ButtonGroup>
                        </FormControl>
                        {campaignKind === 'automation' && (
                          <Text fontSize="xs" color="purple.500" maxW="420px">
                            Company Automation: triggers on every user&apos;s deals and contacts
                            (regardless of owner), stays hidden from non-admins, and never shows
                            as an enrollment on deals or contacts. It is created disabled — enable
                            it from the Campaigns page when ready.
                          </Text>
                        )}
                      </VStack>
                    ) : (
                      <Badge colorScheme="gray" fontSize="2xs" variant="subtle">
                        Personal campaign
                      </Badge>
                    )
                  ) : (
                    <Badge
                      colorScheme={isAutomation ? 'purple' : isPersonalTemplate(template) ? 'gray' : 'blue'}
                      fontSize="2xs"
                      variant="subtle"
                    >
                      {isAutomation
                        ? 'Company automation'
                        : isPersonalTemplate(template) ? 'Personal campaign' : 'Company campaign'}
                    </Badge>
                  )}
                </>
              )}
            </VStack>

            <VStack align="end" spacing={2}>
              <HStack spacing={3}>
                <ButtonGroup size="sm" isAttached variant="outline" data-tour="builder-mode-toggle">
                  <Button
                    leftIcon={<Icon as={FiList} />}
                    colorScheme={builderMode === 'simple' ? 'blue' : 'gray'}
                    variant={builderMode === 'simple' ? 'solid' : 'outline'}
                    onClick={() => setBuilderMode('simple')}
                  >
                    Simple
                  </Button>
                  <Button
                    leftIcon={<Icon as={FiGrid} />}
                    colorScheme={builderMode === 'canvas' ? 'purple' : 'gray'}
                    variant={builderMode === 'canvas' ? 'solid' : 'outline'}
                    onClick={() => setBuilderMode('canvas')}
                  >
                    Canvas
                  </Button>
                </ButtonGroup>
                <Button
                  as={RouterLink}
                  to="/crm/campaigns"
                  variant="outline"
                  size="sm"
                >
                  {readOnly ? 'Back' : 'Cancel'}
                </Button>
                {!readOnly && !!templateId && (
                  <Tooltip label="Duplicate this campaign (duplicates the last saved version)">
                    <Button
                      leftIcon={<FiCopy />}
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDuplicate(true)}
                    >
                      Duplicate
                    </Button>
                  </Tooltip>
                )}
                {!readOnly && (
                  <Button
                    leftIcon={<FiSave />}
                    colorScheme="blue"
                    size="sm"
                    onClick={handleSave}
                    isLoading={saving}
                    isDisabled={!isDirty && !!templateId}
                    data-tour="builder-save"
                  >
                    Save
                  </Button>
                )}
              </HStack>

              {!readOnly && (
                <HStack spacing={2} data-tour="builder-test">
                  <ContactTestPicker
                    value={testContact}
                    onChange={setTestContact}
                  />
                  <Button
                    leftIcon={<Icon as={FiPlay} />}
                    size="sm"
                    colorScheme="green"
                    variant="outline"
                    onClick={testFullRun}
                    isLoading={testRunning === 'full'}
                    isDisabled={!testContact || !nodes.length}
                  >
                    Test All
                  </Button>
                  {Object.keys(testResults).length > 0 && (
                    <Button
                      leftIcon={<Icon as={FiXCircle} />}
                      size="sm"
                      variant="ghost"
                      onClick={clearTestResults}
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    leftIcon={<Icon as={FiUpload} />}
                    size="sm"
                    variant="ghost"
                    onClick={importModal.onOpen}
                    data-tour="builder-import-export"
                  >
                    Import
                  </Button>
                  <Button
                    leftIcon={<Icon as={FiDownload} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const exportData = {
                        template: { name: template?.name, description: template?.description, triggerType: template?.triggerType, triggerConfig: template?.triggerConfig, enrollmentRules: template?.enrollmentRules },
                        nodes: nodes.map(n => ({ id: n.id, subType: n.subType, nodeType: n.nodeType, config: n.config, nextNodeId: n.nextNodeId, falseNodeId: n.falseNodeId, orderIndex: n.orderIndex })),
                        testResults: Object.keys(testResults).length > 0 ? testResults : undefined,
                      };
                      navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
                      toast({ title: 'Workflow JSON copied to clipboard', status: 'info', duration: 2000, isClosable: true });
                    }}
                  >
                    Export
                  </Button>
                </HStack>
              )}
            </VStack>
          </Flex>

          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {builderMode === 'simple' ? (
            <>
              {/* Trigger */}
              <Flex direction="column" align="center" mb={2} data-tour="builder-trigger">
                <TriggerHeader />
              </Flex>

              {/* Linear Node List */}
              <NodeList onNodeClick={handleNodeClick} />

              {nodes.length === 0 && !readOnly && (
                <Text textAlign="center" color="gray.500" mt={4} fontSize="sm">
                  Click the + button above to add your first automation step.
                </Text>
              )}
            </>
          ) : (
            /* Canvas Mode */
            <CanvasBuilder onNodeClick={handleNodeClick} />
          )}

          {!readOnly && fullTestResult?.success && (
            <Box
              mt={6}
              p={4}
              bg={bannerBg}
              border="1px solid"
              borderColor={bannerBorder}
              borderRadius="xl"
            >
              <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <HStack spacing={4}>
                  <Text fontWeight="bold" color="green.600" _dark={{ color: 'green.300' }}>
                    Test Complete
                  </Text>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                    {fullTestResult.totalSteps} steps executed
                  </Text>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                    Est. duration: {fullTestResult.estimatedDuration}
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500">
                    Contact: {fullTestResult.contact?.name}
                  </Text>
                  {fullTestResult.deal && (
                    <Text fontSize="xs" color="gray.500">
                      | Deal: {fullTestResult.deal.name} ({fullTestResult.deal.stage})
                    </Text>
                  )}
                </HStack>
              </Flex>
            </Box>
          )}
        </Container>
      </Box>

      <NodeConfigDrawer
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
        node={selectedNode}
      />

      {/* Duplicate — copies the last SAVED version (fetches from the DB).
          Mounted only while open so the CRM-user list is fetched lazily. */}
      {showDuplicate && template?.id && (
        <DuplicateCampaignModal
          isOpen
          onClose={() => setShowDuplicate(false)}
          template={template}
          onDuplicated={(created, { ownedByMe } = {}) => {
            if (ownedByMe && created?.id) {
              history.push(`/crm/campaigns/builder/${created.id}`);
            }
          }}
        />
      )}

      {!readOnly && (
        <ImportCampaignModal
          isOpen={importModal.isOpen}
          onClose={importModal.onClose}
          hasExistingNodes={nodes.length > 0}
          onImport={(data) => {
            const result = importCampaign(data);
            importModal.onClose();
            toast({
              title: `Imported ${result.nodeCount} step${result.nodeCount === 1 ? '' : 's'}`,
              description: 'Review the campaign, then click Save.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }}
        />
      )}

      {!readOnly && <AlertDialog
        isOpen={!!enablePrompt}
        leastDestructiveRef={cancelRef}
        onClose={() => setEnablePrompt(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {enablePrompt?.isAutomation ? 'Automation Created' : 'Campaign Created'}
            </AlertDialogHeader>
            <AlertDialogBody>
              {enablePrompt?.isAutomation
                ? 'Your Company Automation has been saved. It starts disabled — would you like to enable it now so it begins running on all users\u2019 deals and contacts?'
                : isImpersonating
                  ? `The campaign has been saved and ${effectiveUserName} has been subscribed to it. Would you like to enable it now?`
                  : "Your campaign has been saved and you've been subscribed to it. Would you like to enable it now?"}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={async () => {
                  if (enablePrompt?.userCampaignId) {
                    try {
                      await toggleUserCampaign(enablePrompt.userCampaignId, false);
                    } catch (e) {
                      console.warn('Failed to disable campaign:', e.message);
                    }
                  }
                  setEnablePrompt(null);
                }}
              >
                Not Now
              </Button>
              <Button
                colorScheme="blue"
                ml={3}
                onClick={async () => {
                  if (enablePrompt?.userCampaignId) {
                    try {
                      await toggleUserCampaign(enablePrompt.userCampaignId, true);
                    } catch (e) {
                      console.warn('Failed to enable campaign:', e.message);
                    }
                  }
                  setEnablePrompt(null);
                }}
              >
                Enable
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>}

      {!readOnly && (
        <AlertDialog
          isOpen={showLeave}
          leastDestructiveRef={leaveCancelRef}
          onClose={handleStay}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Unsaved changes
              </AlertDialogHeader>
              <AlertDialogBody>
                You have unsaved changes. Do you want to leave without saving? Your changes will be lost.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={leaveCancelRef} onClick={handleStay}>
                  Go Back
                </Button>
                <Button colorScheme="red" ml={3} onClick={handleLeave}>
                  Leave Without Saving
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </CampaignContext.Provider>
  );
}

export function CampaignViewPage() {
  return <CampaignBuilderPage readOnly />;
}
